// frontend/src/pages/admin/ExternalTokens/index.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity, FileSearch, History, Key, KeyRound, Plus,
} from 'lucide-react';

import { useTabPersistence } from '../../../hooks/usePersistence';
import OverviewTab from './OverviewTab';
import ServiceApiKeysTab from './ServiceApiKeysTab';
import RequestLogsTab from './RequestLogsTab';
import AdminActivityTab from './AdminActivityTab';

const VALID_TABS = ['overview', 'keys', 'logs', 'activity'];

// Legacy deep-links (Asset Inventory, old bookmarks) map onto the new IA.
const LEGACY_TAB_MAP = {
  'service-keys': 'keys',
  'service-api-keys': 'keys',
  'api-keys': 'keys',
  'request-logs': 'logs',
  'admin-activity': 'activity',
};

const ExternalTokensPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useTabPersistence(
    'api-operations',
    'overview',
    VALID_TABS,
  );
  const [logFilters, setLogFilters] = useState({});
  const [keysSearch, setKeysSearch] = useState(() => searchParams.get('search') || '');
  const [keysCreateOpen, setKeysCreateOpen] = useState(false);

  // Normalize legacy ?tab= values once.
  useEffect(() => {
    const raw = searchParams.get('tab');
    if (raw && LEGACY_TAB_MAP[raw]) {
      setActiveTab(LEGACY_TAB_MAP[raw]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const search = searchParams.get('search');
    if (search) setKeysSearch(search);
  }, [searchParams]);

  const adminTabs = useMemo(() => [
    { id: 'overview', name: 'Overview', icon: Activity, description: 'Health at a glance' },
    { id: 'keys', name: 'API Keys', icon: KeyRound, description: 'Credentials & scopes' },
    { id: 'logs', name: 'Request Logs', icon: FileSearch, description: 'Per-request traffic' },
    { id: 'activity', name: 'Admin Activity', icon: History, description: 'Who changed access' },
  ], []);

  const handleNavigateTab = useCallback((tabId, filters = {}) => {
    const next = LEGACY_TAB_MAP[tabId] || tabId;
    setActiveTab(next);
    if (next === 'logs') {
      setLogFilters(filters);
    }
    if (next === 'keys' && filters.search) {
      setKeysSearch(filters.search);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', 'keys');
        params.set('search', filters.search);
        return params;
      }, { replace: true });
    }
  }, [setActiveTab, setSearchParams]);

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab onNavigateTab={handleNavigateTab} />;
      case 'keys':
        return (
          <ServiceApiKeysTab
            initialSearch={keysSearch}
            openCreateSignal={keysCreateOpen}
            onCreateSignalHandled={() => setKeysCreateOpen(false)}
            showToolbarCreate={false}
          />
        );
      case 'logs':
        return <RequestLogsTab initialFilters={logFilters} />;
      case 'activity':
        return <AdminActivityTab />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Key className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">
              API Operations
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
            Monitor external API health, manage Service API Keys, and review request activity.
          </p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              setActiveTab('keys');
              setKeysCreateOpen(true);
            }}
            className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
            aria-label="Generate API Key"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Generate API Key</span>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-2">
        {adminTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
              title={tab.description}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {renderActiveTab()}
    </div>
  );
};

export default ExternalTokensPage;
